import classnames from 'classnames';
import { ClassValue } from 'classnames/types';
import { observer } from 'mobx-react';
import * as React from 'react';

const styles = require('./table.css');

/** Props for a table */
export interface TableProps {
  /** Additional classes to apply */
  className?: string;
  /** Table rows */
  children: React.ReactNode;

  /** Align column 1 right */
  alignRightColumn1?: boolean;
  /** Align column 2 right */
  alignRightColumn2?: boolean;
  /** Align column 3 right */
  alignRightColumn3?: boolean;
  /** Align column 4 right */
  alignRightColumn4?: boolean;
  /** Align column 5 right */
  alignRightColumn5?: boolean;
  /** Align column 6 right */
  alignRightColumn6?: boolean;
  /** Align column 7 right */
  alignRightColumn7?: boolean;
  /** Align column 8 right */
  alignRightColumn8?: boolean;
  /** Align column 8 right */
  alignRightColumn9?: boolean;
  /** Align column 8 right */
  alignRightColumn10?: boolean;

  /** Align column 1 center */
  alignCenterColumn1?: boolean;
  /** Align column 2 center */
  alignCenterColumn2?: boolean;
  /** Align column 3 center */
  alignCenterColumn3?: boolean;
  /** Align column 4 center */
  alignCenterColumn4?: boolean;
  /** Align column 5 center */
  alignCenterColumn5?: boolean;
  /** Align column 6 center */
  alignCenterColumn6?: boolean;
  /** Align column 7 center */
  alignCenterColumn7?: boolean;
  /** Align column 8 center */
  alignCenterColumn8?: boolean;
  /** Align column 8 center */
  alignCenterColumn9?: boolean;
  /** Align column 8 center */
  alignCenterColumn10?: boolean;

  /** Visibility of column 1 after breakpoint */
  column1VisibleAfter?: 'sm';
  /** Visibility of column 2 after breakpoint */
  column2VisibleAfter?: 'sm';
  /** Visibility of column 3 after breakpoint */
  column3VisibleAfter?: 'sm';
  /** Visibility of column 4 after breakpoint */
  column4VisibleAfter?: 'sm';
  /** Visibility of column 5 after breakpoint */
  column5VisibleAfter?: 'sm';
  /** Visibility of column 6 after breakpoint */
  column6VisibleAfter?: 'sm';
  /** Visibility of column 7 after breakpoint */
  column7VisibleAfter?: 'sm';
  /** Visibility of column 8 after breakpoint */
  column8VisibleAfter?: 'sm';
  /** Visibility of column 9 after breakpoint */
  column9VisibleAfter?: 'sm';
  /** Visibility of column 10 after breakpoint */
  column10VisibleAfter?: 'sm';
}

/** Props for a table row */
export interface TableRowProps {
  /** Additional classes to apply */
  className?: string;
  /** Whether this is a header row */
  header?: boolean;
  /** Callback on click */
  onClick?(): void;
  /** Callback on double click */
  onDoubleClick?(): void;
  /** Table columns */
  children: React.ReactNode;
}

/** Props for a table cell */
export interface TableCellProps {
  /** Additional classes to apply */
  className?: string;
  /** Cell contents */
  children?: React.ReactNode;

  /** Whether bordered (of rhs except last column) */
  bordered?: boolean;
}

/**
 * A table row, similar to `tr` HTML element.
 */
@observer
export class TableRow extends React.Component<TableRowProps> {
  /** Renders the component */
  render() {
    const children = childrenAsArray(this.props.children);

    return (
      <tr
        className={classnames(
          styles.tableRow,

          children.length === 2 && styles.twoColumns,
          children.length === 3 && styles.threeColumns,
          children.length === 4 && styles.fourColumns,
          this.props.onClick && styles.clickable,
          this.props.className
        )}
        onClick={this.props.onClick}
        onDoubleClick={this.props.onDoubleClick}
      >
        {this.props.children}
      </tr>
    );
  }
}

/**
 * Table component, to be used as the outermost component of a table
 * definition, similar to `table` HTML element.
 */
@observer
export class Table extends React.Component<TableProps> {
  manageAlignments(numCols: number): ClassValue[] {
    const {
      alignRightColumn1,
      alignRightColumn2,
      alignRightColumn3,
      alignRightColumn4,
      alignRightColumn5,
      alignRightColumn6,
      alignRightColumn7,
      alignRightColumn8,
      alignRightColumn9,
      alignRightColumn10,

      alignCenterColumn1,
      alignCenterColumn2,
      alignCenterColumn3,
      alignCenterColumn4,
      alignCenterColumn5,
      alignCenterColumn6,
      alignCenterColumn7,
      alignCenterColumn8,
      alignCenterColumn9,
      alignCenterColumn10
    } = this.props;
    const alignClasses = [];
    /// First do centre, then do right
    if (numCols > 0) {
      if (alignCenterColumn1) {
        alignClasses.push(styles.alignCenterColumn1);
      } else if (alignRightColumn1) {
        alignClasses.push(styles.alignRightColumn1);
      }
    }
    if (numCols > 1) {
      if (alignCenterColumn2) {
        alignClasses.push(styles.alignCenterColumn2);
      } else if (alignRightColumn2) {
        alignClasses.push(styles.alignRightColumn2);
      }
    }
    if (numCols > 2) {
      if (alignCenterColumn3) {
        alignClasses.push(styles.alignCenterColumn3);
      } else if (alignRightColumn3) {
        alignClasses.push(styles.alignRightColumn3);
      }
    }
    if (numCols > 3) {
      if (alignCenterColumn4) {
        alignClasses.push(styles.alignCenterColumn4);
      } else if (alignRightColumn4) {
        alignClasses.push(styles.alignRightColumn4);
      }
    }
    if (numCols > 4) {
      if (alignCenterColumn5) {
        alignClasses.push(styles.alignCenterColumn5);
      } else if (alignRightColumn5) {
        alignClasses.push(styles.alignRightColumn5);
      }
    }
    if (numCols > 5) {
      if (alignCenterColumn6) {
        alignClasses.push(styles.alignCenterColumn6);
      } else if (alignRightColumn6) {
        alignClasses.push(styles.alignRightColumn6);
      }
    }
    if (numCols > 6) {
      if (alignCenterColumn7) {
        alignClasses.push(styles.alignCenterColumn7);
      } else if (alignRightColumn7) {
        alignClasses.push(styles.alignRightColumn7);
      }
    }
    if (numCols > 7) {
      if (alignCenterColumn8) {
        alignClasses.push(styles.alignCenterColumn8);
      } else if (alignRightColumn8) {
        alignClasses.push(styles.alignRightColumn8);
      }
    }
    if (numCols > 8) {
      if (alignCenterColumn9) {
        alignClasses.push(styles.alignCenterColumn9);
      } else if (alignRightColumn9) {
        alignClasses.push(styles.alignRightColumn9);
      }
    }
    if (numCols > 9) {
      if (alignCenterColumn10) {
        alignClasses.push(styles.alignCenterColumn10);
      } else if (alignRightColumn10) {
        alignClasses.push(styles.alignRightColumn10);
      }
    }
    return alignClasses;
  }

  /** Partitions the rows into header and body rows */
  partitionRows(): { headerRows: JSX.Element[]; bodyRows: JSX.Element[] } {
    const headerRows: JSX.Element[] = [];
    const bodyRows: JSX.Element[] = [];
    for (const child of childrenAsArray(this.props.children)) {
      if (typeof child === 'string' || typeof child === 'number' || child.type !== TableRow) {
        throw new Error('Only table row children are supported');
      }

      const props = child.props as TableRowProps;
      if (props.header) {
        headerRows.push(child);
      } else {
        bodyRows.push(child);
      }
    }

    return { headerRows, bodyRows };
  }

  /** Renders the component */
  render() {
    const { headerRows, bodyRows } = this.partitionRows();

    /// Get the max row children (column) count, for both heade and body rows...
    const maxColumnCount = [...headerRows, ...bodyRows].reduce((acc, next) => {
      const colCount = childrenAsArray(next.props.children).length;
      return colCount > acc ? colCount : acc;
    }, 0);
    /// ...and then set the alignment classNames
    const alignmentClasses = this.manageAlignments(maxColumnCount);

    return (
      // NOTE: Container div is required to have rounded outer border because
      // border-radius does not work with collapsing borders
      <div
        className={classnames(
          styles.tableContainer,
          ...alignmentClasses,
          this.props.column1VisibleAfter === 'sm' && styles.column1VisibleAfterSm,
          this.props.column2VisibleAfter === 'sm' && styles.column2VisibleAfterSm,
          this.props.column3VisibleAfter === 'sm' && styles.column3VisibleAfterSm,
          this.props.column4VisibleAfter === 'sm' && styles.column4VisibleAfterSm,
          this.props.column5VisibleAfter === 'sm' && styles.column5VisibleAfterSm,
          this.props.column6VisibleAfter === 'sm' && styles.column6VisibleAfterSm,
          this.props.column7VisibleAfter === 'sm' && styles.column7VisibleAfterSm,
          this.props.column8VisibleAfter === 'sm' && styles.column8VisibleAfterSm,
          this.props.column9VisibleAfter === 'sm' && styles.column9VisibleAfterSm,
          this.props.column10VisibleAfter === 'sm' && styles.column10VisibleAfterSm,

          this.props.className
        )}
      >
        <table className={styles.table}>
          {headerRows.length > 0 ? <thead className={styles.thead}>{headerRows}</thead> : undefined}
          {bodyRows.length > 0 ? <tbody className={styles.tbody}>{bodyRows}</tbody> : undefined}
        </table>
      </div>
    );
  }
}

/**
 * A table cell, similar to a `td` or `th` HTML element.
 */
@observer
export class TableCell extends React.Component<TableCellProps> {
  /** Renders the component */
  render() {
    return (
      <td className={classnames(this.props.className, styles.tableCell, this.props.bordered && styles.columnBordered)}>
        {this.props.children}
      </td>
    );
  }
}

/** Puts children (which may not be an array) into an array */
function childrenAsArray(children: React.ReactNode): React.ReactChild[] {
  const array: React.ReactChild[] = [];
  React.Children.forEach(children, child => {
    array.push(child as React.ReactChild);
  });
  return array;
}



// WEBPACK FOOTER //
// ./src/ui/widgets/table/table.tsx